import { Plus, Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useClientsInfinite } from '../../hooks/use-clients';
import type { ClientEntity } from '../../lib/clients-api';
import { Input } from './form-primitives';
import { PillButton } from './PillButton';

interface ClientPickerProps {
  value: ClientEntity | null;
  onChange: (client: ClientEntity | null) => void;
  onCreateNew?: () => void;
  placeholder?: string;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export function ClientPicker({
  value,
  onChange,
  onCreateNew,
  placeholder = 'Buscar por nome ou CPF/CNPJ…',
}: ClientPickerProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const debouncedQ = useDebouncedValue(query.trim(), 250);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const listQ = useClientsInfinite(debouncedQ.length >= 2 ? { q: debouncedQ } : {});
  const items = listQ.data?.pages.flatMap((p) => p.items) ?? [];
  const showResults = open && debouncedQ.length >= 2;

  useEffect(() => {
    function onDocClick(e: MouseEvent): void {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  if (value) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-[10px] border border-line bg-panel-2 p-3">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-medium text-ink">{value.name}</div>
          <div className="truncate text-[11px] text-muted-2">
            {value.document ?? 'sem documento'}
            {value.email ? ` · ${value.email}` : ''}
          </div>
        </div>
        <button
          type="button"
          aria-label="Trocar cliente"
          onClick={() => onChange(null)}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-ink"
        >
          <X size={14} strokeWidth={1.8} />
        </button>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative flex flex-col gap-2">
      <div className="relative">
        <Search
          size={14}
          strokeWidth={2}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-2"
          aria-hidden
        />
        <Input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          className="pl-9"
        />
      </div>

      {showResults && (
        <div className="absolute left-0 right-0 top-12 z-30 max-h-[280px] overflow-y-auto rounded-[10px] border border-line bg-panel shadow-lg">
          {listQ.isLoading ? (
            <div className="px-3 py-4 text-center text-[12px] text-muted">Buscando…</div>
          ) : items.length === 0 ? (
            <div className="px-3 py-4 text-center text-[12px] text-muted">
              Nenhum cliente encontrado.
            </div>
          ) : (
            <ul className="py-1">
              {items.slice(0, 10).map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(c);
                      setQuery('');
                      setOpen(false);
                    }}
                    className="flex w-full flex-col items-start gap-[2px] px-3 py-2 text-left text-[12px] transition-colors hover:bg-panel-2"
                  >
                    <span className="font-medium text-ink">{c.name}</span>
                    <span className="text-[11px] text-muted-2">
                      {c.document ?? 'sem documento'}
                      {c.email ? ` · ${c.email}` : ''}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {onCreateNew && (
        <PillButton type="button" icon={<Plus size={12} strokeWidth={2} />} onClick={onCreateNew}>
          Criar cliente novo
        </PillButton>
      )}
    </div>
  );
}
